// ER-3062 (Loi Koopkracht): display helpers of the promotions of a simulated product.
//
// Every discount line returned by hgz-proxy-service in `discounts[]` is a promotion, conditional ([NA] / [NA-DD] /
// [DD]) or not: the display does not tell them apart. Its `promoType` (NA, NA-DD, DD or default) selects the text of
// its "Infos et conditions" window, managed by the business in Hypermega Sales > Promo conditions (stored by
// configuration-service) and sent with every product in `labels.promo_conditions` as `{fr, nl}` objects. A text may
// hold the placeholders {product}, {date} (MM/YYYY) and {amount}, **bold** parts and [BR] line breaks.

/**
 * Promotions of one product or of a list of products: one entry per discount line (positive amounts), the lines
 * without amount being skipped. `amountKey` is the amount to read, `priceEuro` (VAT excluded) or `priceEuroVAT`.
 * Without the `discounts` detail (feature flag off), the aggregated discount of the subtotal is the only promotion.
 */
export function promoLines(products, amountKey) {
  let lines = [];

  [].concat(products).forEach(product => {
    if (!product) {
      return;
    }

    if (Array.isArray(product.discounts)) {
      product.discounts
        .filter(discount => discount[amountKey])
        .forEach(discount => lines.push({
          promoType: discount.promoType || discount.conditionCode || "default",
          label: discount.label || "",
          amount: Math.abs(discount[amountKey])
        }));
    } else if (product.subtotal && product.subtotal.discount && product.subtotal.discount[amountKey]) {
      lines.push({ promoType: "default", label: "", amount: Math.abs(product.subtotal.discount[amountKey]) });
    }
  });

  return lines;
}

/**
 * Regroups the promotions of the energies of a card (gas and electricity are usually offered together): one entry per
 * promo type, the annual amounts summed. The Haugazel label is kept only when all the grouped lines share it.
 */
export function groupPromoLines(lines) {
  let groups = [];

  lines.forEach(line => {
    const group = groups.find(item => item.promoType === line.promoType);

    if (!group) {
      groups.push({ ...line });
      return;
    }

    group.amount += line.amount;
    if (group.label !== line.label) {
      group.label = "";
    }
  });

  return groups;
}

/**
 * Raw text of the "Infos et conditions" window of a promo type in the given language, null when no product of the
 * list carries one (not written yet, or not in this language).
 */
export function promoText(products, promoType, locale) {
  const found = [].concat(products)
    .map(product => product && product.labels && product.labels.promo_conditions && product.labels.promo_conditions[promoType]
      && product.labels.promo_conditions[promoType][locale])
    .find(text => typeof text === "string" && text.trim() !== "");

  return found || null;
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Safe HTML of a business text: the placeholders are replaced first, then everything is escaped and only
 * the `**bold**` markup, the [BR] code and the line breaks are turned into tags. A known placeholder without value is left empty, any other braces stay as written.
 */
export function renderPromoText(text, values) {
  const filled = String(text).replace(/\{(product|date|amount)\}/g, (match, key) => (values && values[key] !== undefined ? values[key] : ""));

  return escapeHtml(filled)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[BR\]/gi, "<br>")
    .replace(/\r?\n/g, "<br>");
}

/**
 * "€200" or "€50,63": the cents are only shown when there are some.
 */
export function formatEuro(amount) {
  const rounded = Math.round(Math.abs(amount) * 100) / 100;

  return "€" + (rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2).replace(".", ","));
}

/**
 * Month of the simulation, "MM/YYYY".
 */
export function monthYear(date) {
  const day = date || new Date();

  return String(day.getMonth() + 1).padStart(2, "0") + "/" + day.getFullYear();
}



// WEBPACK FOOTER //
// ./src/helpers/promoDisplay.js